import { Outlet } from 'react-router-dom';
import { LogOut, User, Sun, Moon, ChevronDown } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import Sidebar from './Sidebar';
import NotificationsDropdown from './NotificationsDropdown';
import GlobalSearch from './GlobalSearch';
import { useAuth } from '@/auth/AuthProvider';
import { useUIStore } from '@/store/ui';

export default function Layout() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useUIStore();

  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="flex h-screen overflow-hidden bg-surface-50 dark:bg-slate-950">
      <Sidebar />

      <div className="flex flex-1 flex-col overflow-hidden">
        {/* ── Top bar ───────────────────────────────────────── */}
        <header className="flex h-16 shrink-0 items-center justify-between bg-white px-6 shadow-topbar dark:bg-slate-900 dark:shadow-none dark:border-b dark:border-slate-800/60">
          <div className="w-40" />

          <div className="flex flex-1 justify-center">
            <GlobalSearch />
          </div>

          {/* ── Right-side actions ──────────────────────────── */}
          <div className="flex items-center gap-1">
            <button
              onClick={toggleTheme}
              className="group rounded-lg p-2 text-slate-400 transition-all duration-200 hover:bg-slate-100 hover:text-slate-600 dark:text-slate-500 dark:hover:bg-slate-800 dark:hover:text-slate-300"
              title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? (
                <Sun className="h-[18px] w-[18px] transition-transform duration-300 group-hover:rotate-45" />
              ) : (
                <Moon className="h-[18px] w-[18px] transition-transform duration-300 group-hover:-rotate-12" />
              )}
            </button>

            <NotificationsDropdown />

            <div className="relative ml-2" ref={menuRef}>
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 rounded-lg px-2 py-1.5 transition-all duration-200 hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-600 text-xs font-semibold text-white ring-2 ring-brand-100 dark:ring-brand-900">
                  {user?.initials ?? 'U'}
                </div>
                <span className="hidden text-sm font-medium text-slate-700 dark:text-slate-300 sm:block">
                  {user?.name ?? 'User'}
                </span>
                <ChevronDown className="hidden h-3.5 w-3.5 text-slate-400 sm:block" />
              </button>

              {userMenuOpen && (
                <div className="animate-scale-in absolute right-0 top-full z-50 mt-2 w-56 overflow-hidden rounded-xl bg-white shadow-dropdown dark:bg-slate-800 dark:ring-1 dark:ring-slate-700">
                  <div className="border-b border-slate-100 px-4 py-3 dark:border-slate-700/60">
                    <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{user?.name}</p>
                    <p className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400">{user?.email}</p>
                  </div>
                  <div className="p-1.5">
                    <button className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-slate-600 transition-colors duration-150 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-700/60">
                      <User className="h-4 w-4 text-slate-400" />
                      Profile
                    </button>
                    <button
                      onClick={() => {
                        setUserMenuOpen(false);
                        logout();
                      }}
                      className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-red-600 transition-colors duration-150 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40"
                    >
                      <LogOut className="h-4 w-4" />
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* ── Page content ──────────────────────────────────── */}
        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-[1400px] animate-slide-up px-8 py-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
